/**
 * Typed client for the Supplier Pricing & Procurement Connectors surface.
 *
 * Routes (beam-backend pricingRouter.ts, /api, Bearer):
 *   GET  /api/price-connectors           ?trade&kind&search → ConnectorRow[]  (GLOBAL directory)
 *   GET  /api/price-connectors/:id                          → ConnectorRow
 *   GET  /api/supplier-connections       ?status            → ConnectionRow[]
 *   POST /api/supplier-connections       { connector_id?|connector_code?, supplier_id?, account_number?, config? }
 *   GET  /api/supplier-connections/:id                      → ConnectionRow
 *   POST /api/supplier-connections/:id/link    { secrets?, accountNumber?, capabilities?, config? }
 *   POST /api/supplier-connections/:id/unlink
 *   GET  /api/supplier-connections/:id/health
 *   POST /api/supplier-connections/:id/sync
 *   GET  /api/price-imports              ?status            → PriceImportRow[]
 *   GET  /api/price-imports/:id                             → PriceImportRow
 *   POST /api/price-imports/:id/confirm
 *   POST /api/price-imports/:id/reject
 *
 * These endpoints return raw Prisma rows (snake_case); we type them locally rather than
 * adding a zod DTO for every shape. Bearer token via src/api/client.ts.
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { get, post } from './client';

// ---------------------------------------------------------------------------
// Row types (snake_case wire shapes)
// ---------------------------------------------------------------------------

export type ConnectorKind = 'distributor' | 'manufacturer' | 'retail' | 'aggregator' | string;
export type ConnectorLinkMethod = 'api_key' | 'punchout_shared_secret' | 'edi_partner_id' | string;
export type ConnectionStatus = 'unlinked' | 'pending' | 'linked' | 'error' | 'expired' | string;
export type PriceImportStatus = 'pending' | 'confirmed' | 'applied' | 'rejected' | string;

export interface ConnectorRow {
  id: string;
  code: string;
  display_name: string;
  kind: ConnectorKind;
  mechanism: string;
  max_capability_tier: number;
  link_method: ConnectorLinkMethod;
  status: string;
  trades_supported: string[];
}

export interface ConnectionRow {
  id: string;
  connector_id: string | null;
  connector_code: string | null;
  display_name: string | null;
  supplier_id: string | null;
  account_number: string | null;
  status: ConnectionStatus;
  capabilities: string[] | null;
  link_method: ConnectorLinkMethod | null;
  last_synced_at: string | null;
  config: Record<string, unknown> | null;
  created_at: string | null;
}

export interface PriceImportRow {
  id: string;
  supplier_connection_id: string | null;
  tier: number | null;
  status: PriceImportStatus;
  line_count: number | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface ListConnectorsParams {
  trade?: string;
  kind?: string;
  search?: string;
}

export interface CreateConnectionRequest {
  connector_id?: string;
  connector_code?: string;
  supplier_id?: string;
  account_number?: string;
  config?: Record<string, unknown>;
}

export interface LinkConnectionRequest {
  secrets?: {
    api_key?: string;
    punchout_shared_secret?: string;
    edi_partner_id?: string;
  };
  accountNumber?: string;
  capabilities?: string[];
  config?: Record<string, unknown>;
}

export interface ListConnectionsParams {
  status?: string;
}

export interface ListPriceImportsParams {
  status?: string;
}

// ---------------------------------------------------------------------------
// Connector directory (global)
// ---------------------------------------------------------------------------

/** List the global connector directory, optionally filtered by trade / kind / search. */
export async function listConnectors(
  params: ListConnectorsParams = {},
): Promise<ConnectorRow[]> {
  const qs = new URLSearchParams();
  if (params.trade) qs.set('trade', params.trade);
  if (params.kind) qs.set('kind', params.kind);
  if (params.search) qs.set('search', params.search);
  const query = qs.toString();
  return get<ConnectorRow[]>(`/api/price-connectors${query ? '?' + query : ''}`);
}

/** Fetch a single connector from the directory. */
export async function getConnector(id: string): Promise<ConnectorRow> {
  return get<ConnectorRow>(`/api/price-connectors/${id}`);
}

// ---------------------------------------------------------------------------
// Supplier connections (per-org)
// ---------------------------------------------------------------------------

/** List the org's supplier connections, optionally filtered by status. */
export async function listConnections(
  params: ListConnectionsParams = {},
): Promise<ConnectionRow[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  const query = qs.toString();
  return get<ConnectionRow[]>(`/api/supplier-connections${query ? '?' + query : ''}`);
}

/** Create a supplier connection from a directory connector. */
export async function createConnection(
  body: CreateConnectionRequest,
): Promise<ConnectionRow> {
  return post<ConnectionRow>('/api/supplier-connections', body);
}

/** Fetch a single supplier connection. */
export async function getConnection(id: string): Promise<ConnectionRow> {
  return get<ConnectionRow>(`/api/supplier-connections/${id}`);
}

/** Link a connection — provide the secret appropriate to the connector's link_method. */
export async function linkConnection(
  id: string,
  body: LinkConnectionRequest,
): Promise<ConnectionRow> {
  return post<ConnectionRow>(`/api/supplier-connections/${id}/link`, body);
}

/** Unlink (deactivate credentials for) a connection. */
export async function unlinkConnection(id: string): Promise<ConnectionRow> {
  return post<ConnectionRow>(`/api/supplier-connections/${id}/unlink`);
}

/** Run a health/credential check on a connection. */
export async function healthConnection(id: string): Promise<unknown> {
  return get<unknown>(`/api/supplier-connections/${id}/health`);
}

/** Trigger a price sync for a connection. */
export async function syncConnection(id: string): Promise<unknown> {
  return post<unknown>(`/api/supplier-connections/${id}/sync`);
}

// ---------------------------------------------------------------------------
// MMI market-reference pricing (always-on market source feeding estimate/bid drafts)
// ---------------------------------------------------------------------------

export interface MmiStatusRow {
  configured: boolean;
  connected: boolean;
  last_sync_at: string | null;
  reference_prices: number;
  health: { ok: boolean; detail?: string };
}

export interface MmiSyncResult {
  enabled: boolean;
  materialsConsidered: number;
  matched: number;
  updated: number;
  skipped: number;
  message?: string;
}

/** Market-pricing (MMI) status: configured / connected / last sync / how many reference prices / live health. */
export async function mmiStatus(): Promise<MmiStatusRow> {
  return get<MmiStatusRow>('/api/mmi/status');
}

/** Pull the latest MMI market prices into the catalog (owner/office). Used to price estimate/bid drafts. */
export async function mmiSync(): Promise<MmiSyncResult> {
  return post<MmiSyncResult>('/api/mmi/sync');
}

// ---------------------------------------------------------------------------
// Price imports
// ---------------------------------------------------------------------------

/** List price imports, optionally filtered by status. */
export async function listPriceImports(
  params: ListPriceImportsParams = {},
): Promise<PriceImportRow[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  const query = qs.toString();
  return get<PriceImportRow[]>(`/api/price-imports${query ? '?' + query : ''}`);
}

/** Fetch a single price import. */
export async function getPriceImport(id: string): Promise<PriceImportRow> {
  return get<PriceImportRow>(`/api/price-imports/${id}`);
}

/** Confirm a pending price import (applies the staged prices). */
export async function confirmPriceImport(id: string): Promise<PriceImportRow> {
  return post<PriceImportRow>(`/api/price-imports/${id}/confirm`);
}

/** Reject a pending price import. */
export async function rejectPriceImport(id: string): Promise<PriceImportRow> {
  return post<PriceImportRow>(`/api/price-imports/${id}/reject`);
}
